import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/brand/BrandButton";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ArrowRight, Menu } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { localizePath, stripLocalePrefix } from "@/lib/localeRoutes";
import { usePostHog } from "@/analytics/usePostHog";
import { useAppUrl } from "@/hooks/useAppUrl";
import { ResourcesNavigation } from "./resources/ResourcesNavigation";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const posthog = usePostHog();
  const appUrl = useAppUrl();
  const currentPath = stripLocalePrefix(location.pathname);

  const getPath = (path: string) => localizePath(path, i18n.language);
  const isActive = (path: string) =>
    path === "/"
      ? currentPath === "/"
      : currentPath === path || currentPath.startsWith(`${path}/`);

  const navigationItems = [
    { path: "/doctor-visits", label: t("navigation.doctorVisits") },
    { path: "/resources", label: t("navigation.freeResources") },
    { path: "/how-it-works", label: t("navigation.howItWorks") },
    { path: "/why-docio", label: t("navigation.whyDocio") },
    { path: "/contact", label: t("navigation.contact") },
    { path: "/about", label: t("navigation.about") },
  ];

  return (
    <nav
      className="marketing-navigation border-b border-border bg-card sticky top-0 z-50"
      aria-label={t("navigation.menu")}
    >
      <div className="mx-auto max-w-[1248px] px-4 sm:px-6 md:px-10 2xl:max-w-[1440px]">
        <div className="flex flex-wrap justify-between items-center gap-2 min-h-20 py-2">
          {/* Logo */}
          <Link
            to={getPath("/")}
            aria-label={`Docio: ${t("navigation.home")}`}
            aria-current={currentPath === "/" ? "page" : undefined}
            className="flex shrink-0 items-center justify-center min-h-[44px] min-w-[44px] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <img
              src="/assets/docio-logo.webp"
              srcSet="/assets/docio-logo-115.webp 115w, /assets/docio-logo-230.webp 230w, /assets/docio-logo.webp 383w"
              sizes="115px"
              alt=""
              width={383}
              height={160}
              decoding="async"
              className="h-auto w-[115px] max-w-[30vw] shrink-0"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="navigation-desktop hidden items-center gap-6 2xl:gap-8 text-[0.8125rem]">
            {navigationItems.map((item) =>
              item.path === "/resources" ? (
                <ResourcesNavigation key={item.path} />
              ) : (
                <Link
                  key={item.path}
                  to={getPath(item.path)}
                  aria-current={isActive(item.path) ? "page" : undefined}
                  className={`flex min-h-11 items-center whitespace-nowrap rounded-sm transition-colors focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    isActive(item.path)
                      ? "text-primary font-medium"
                      : "text-foreground/80 hover:text-primary"
                  }`}
                >
                  {item.label}
                </Link>
              ),
            )}
          </div>

          {/* Desktop Actions */}
          <div className="navigation-desktop hidden items-center space-x-4 shrink-0">
            <LanguageSelector />
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              asChild
            >
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  posthog?.capture("cta_clicked", {
                    location: "nav",
                    cta_type: "book_visit",
                  })
                }
              >
                {t("navigation.bookVisit")}<ArrowRight aria-hidden="true" className="h-4 w-4" />
              </a>
            </Button>
          </div>

          {/* Tablet Actions */}
          <div className="navigation-tablet hidden items-center space-x-3 md:flex">
            <LanguageSelector />
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              asChild
            >
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  posthog?.capture("cta_clicked", {
                    location: "nav",
                    cta_type: "book_visit",
                  })
                }
              >
                {t("navigation.bookVisit")}<ArrowRight aria-hidden="true" className="h-4 w-4" />
              </a>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div
            data-testid="mobile-navigation"
            className="flex items-center space-x-2 md:hidden"
          >
            <LanguageSelector />
            <Sheet open={isOpen} onOpenChange={open => { setIsOpen(open); posthog?.capture('menu_toggled', { open }); }}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-[44px] min-h-[44px] w-[44px] shrink-0 px-2 py-0"
                  aria-label={t("navigation.openMenu")}
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                className="overflow-y-auto"
                aria-describedby={undefined}
                closeLabel={t("navigation.closeMenu")}
              >
                <SheetTitle className="sr-only">
                  {t("navigation.menu")}
                </SheetTitle>
                <div className="flex min-w-0 flex-col gap-1 mt-8">
                  {navigationItems.map((item) =>
                    item.path === "/resources" ? (
                      <ResourcesNavigation
                        key={item.path}
                        mobile
                        onNavigate={() => setIsOpen(false)}
                      />
                    ) : (
                      <Link
                        key={item.path}
                        to={getPath(item.path)}
                        aria-current={isActive(item.path) ? "page" : undefined}
                        onClick={() => setIsOpen(false)}
                        className={`flex min-w-0 min-h-11 items-center break-words rounded-sm py-2 text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                          isActive(item.path)
                            ? "text-primary font-medium"
                            : "text-foreground/80"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ),
                  )}
                  <Button
                    className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                    asChild
                  >
                    <a
                      href={appUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setIsOpen(false);
                        posthog?.capture("cta_clicked", {
                          location: "nav",
                          cta_type: "book_visit",
                        });
                      }}
                    >
                      {t("navigation.bookVisit")}<ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Tablet Navigation */}
        <div
          data-testid="tablet-navigation"
          className="navigation-tablet hidden md:flex items-center gap-6 overflow-x-auto whitespace-nowrap border-t text-sm"
        >
          {navigationItems.map((item) =>
            item.path === "/resources" ? (
              <ResourcesNavigation key={item.path} />
            ) : (
              <Link
                key={item.path}
                to={getPath(item.path)}
                aria-current={isActive(item.path) ? "page" : undefined}
                className={`shrink-0 rounded-sm py-3 transition-colors focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isActive(item.path)
                    ? "font-medium text-primary"
                    : "text-foreground/80 hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </div>
      </div>
    </nav>
  );
};
